import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { fileToDataUrl } from "../../lib/files";
import { toast } from "sonner";
import { Camera, Send } from "lucide-react";
import { EmailVerifyNotice } from "../../components/EmailVerifyNotice";
import { MfaSettings } from "../../components/MfaSettings";
import { PasswordChange } from "../../components/PasswordChange";

export default function AgentProfile() {
  const [me, setMe] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", territory: "", photo_url: "", commission_pref: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await http.get("/crm/me/agent");
    setMe(data);
    setF({
      first_name: data.profile?.first_name || "", last_name: data.profile?.last_name || "",
      phone: data.profile?.phone || "", territory: data.profile?.territory || "",
      photo_url: data.profile?.photo_url || "", commission_pref: data.commission_pref || "",
    });
  };
  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Carica un'immagine.");
    if (file.size > 3 * 1024 * 1024) return toast.error("Immagine troppo grande (max 3MB).");
    const dataUrl = await fileToDataUrl(file);
    setF((s) => ({ ...s, photo_url: dataUrl }));
  };

  const save = async () => {
    setBusy(true);
    try { await http.put("/crm/me/agent/profile", f); await load(); toast.success("Profilo salvato."); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await http.put("/crm/me/agent/profile", f);
      await http.post("/crm/me/agent/submit");
      await load();
      toast.success("Profilo inviato per l'approvazione.");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  if (!me) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
  const canSubmit = ["profile_incomplete", "integrazioni_richieste", "candidatura_rifiutata"].includes(me.status);

  return (
    <div data-testid="agent-profile-page" className="max-w-3xl mx-auto">
      <h1 className="font-serif text-3xl mb-1">Il tuo profilo</h1>
      <p className="text-sm text-neutral-500 mb-6">Compila i dati, le zone in cui operi e una foto. Poi invia per l'approvazione.</p>

      <div className="card-clean p-5 mb-5">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-neutral-100 flex items-center justify-center shrink-0 border" style={{ borderColor: "var(--border-silver)" }}>
            {f.photo_url ? <img src={f.photo_url} alt="foto" className="w-full h-full object-cover" data-testid="agent-photo-preview" />
              : <Camera className="w-6 h-6 text-neutral-400" />}
          </div>
          <div>
            <label className="px-3 py-2 rounded-md border text-sm cursor-pointer inline-block" style={{ borderColor: "var(--brand-primary)", color: "var(--brand-dark)" }} data-testid="agent-photo-label">
              {f.photo_url ? "Sostituisci foto" : "Carica foto"}
              <input type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="agent-photo-input" />
            </label>
            <p className="text-[11px] text-neutral-400 mt-1">Obbligatoria prima dell'invio.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Field label="Nome" value={f.first_name} onChange={on("first_name")} testid="ap-first" />
          <Field label="Cognome" value={f.last_name} onChange={on("last_name")} testid="ap-last" />
          <Field label="Telefono" value={f.phone} onChange={on("phone")} testid="ap-phone" />
          <Field label="Territorialità operativa" value={f.territory} onChange={on("territory")} testid="ap-territory" />
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Preferenza provvigione</span>
            <select value={f.commission_pref} onChange={on("commission_pref")} data-testid="ap-commission-pref"
              className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}>
              <option value="">— seleziona —</option>
              <option value="fisso">Fisso</option>
              <option value="percentuale">Percentuale</option>
            </select>
          </label>
          <div className="flex items-end">
            <span className="text-[11px] text-neutral-400">L'amministratore conferma condizioni e decorrenza nell'accordo.</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={save} disabled={busy} data-testid="agent-profile-save"
            className="px-4 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }}>Salva bozza</button>
          <button onClick={submit} disabled={busy || !canSubmit} data-testid="agent-profile-submit"
            className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2 disabled:opacity-50" style={{ background: "var(--brand-dark)" }}>
            <Send className="w-4 h-4" /> Invia per approvazione
          </button>
        </div>
        {!me.email_verified && <EmailVerifyNotice email={me.email} />}
        <PasswordChange />
        <MfaSettings />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">{label}</span>
      <input value={value} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
    </label>
  );
}
